import { type CallHomePOS } from '@solink/vd-data-layer/api-clients/callhome/types';
import {
  type CallhomeCamera,
  type CallhomeCameraGroup,
  type CallhomeStream,
  type DeviceId,
  type SiteId,
  type VMSCameraId,
} from '@solinkcorp/api-types';
import { type CameraDto } from '@solinkcorp/callhome';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { DeviceProxyApi } from 'clients';
import { type CallhomeCameraLink } from 'clients/CallhomeApiTypes';
import { type EventCamera } from 'clients/PlatformApi';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import difference from 'lodash/difference';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import moment from 'moment';
import { timeout } from 'promise-tools';
import { is360Camera, isPtzCamera, isPvmCamera } from 'src/core/video/coreCameraUtils';
import type * as LocationUtil from 'utils/LocationUtil';
import { getLocalDate, getLocalTime } from 'utils/PlayerUtil';
import * as constants from 'vd-constants';
import {
  DWELL_TIME_ZONE_TYPES,
  MOTION_TYPES,
  MOTION_TYPE_TO_VMS_DETECTION_TYPE,
  type MotionType,
  OWNER_TYPE,
  ROI_EMPTY,
} from 'vd-constants';
import { type VMSCamera } from '../@solink/vd-data-layer/api-clients/vms/vmsTypes';
import FeaturesUtil from './FeaturesUtil';
import {
  type CamerasDictionaryCamera,
  type VideoDiscoveryCameraGroup,
  type VideoDiscoverySite,
} from './types';
export { type CallhomeCamera } from '@solinkcorp/api-types';

/**
 * Time to wait when trying to load a motion zone for a camera.
 */
const MOTION_ZONE_LOAD_TIMEOUT = 2000;

/**
 * This function gets the longest retention duration given a camera or streams.
 * @param {CallhomeCamera} cam
 * @param {VideoDiscoveryLocation} location
 * @returns {string} a status for the camera
 */
export function cameraStatus(cam: CamerasDictionaryCamera, location?: VideoDiscoverySite) {
  const loc = location || cam.location;
  const locationListUpdateTime = loc.lastUpdateTime;

  if (!locationListUpdateTime) {
    console.error('locationListUpdateTime is not defined');
  }

  const deviceMissedCheckIn = loc
    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      new Date(locationListUpdateTime).getTime() - new Date(loc.nextCheckin).getTime() >
      constants.LOCATION_MISSED_CHECKIN_THRESHOLD_MS
    : false;

  return deviceMissedCheckIn ? 'deviceOffline' : cam.status;
}

export function isDeviceOnline(cam: CamerasDictionaryCamera, location: VideoDiscoverySite) {
  return cameraStatus(cam, location) === 'online';
}

export function is360(
  cam: Pick<CallhomeCamera, 'name'> & { cameraTagIds?: readonly string[] },
): boolean {
  return !!cam && !!cam.name && is360Camera(cam);
}

export function isPvm(cam: { cameraTagIds?: CallhomeCamera['cameraTagIds'] }): boolean {
  return !!cam && isPvmCamera(cam);
}

/** Camera types whose video makes AI analytics unreliable, so AI cannot be configured. */
export function isAiUnsupported(cam: {
  name: string;
  cameraTagIds?: CallhomeCamera['cameraTagIds'];
}): boolean {
  return is360(cam) || isPvm(cam);
}

/** Hardware capability only, regardless of whether the user has enabled PTZ controls. */
export function isPtzCapable(cam: { name: string; ptzCapabilities?: unknown | null }): boolean {
  return !!cam && isPtzCamera(cam);
}

/** True when the camera is both PTZ-capable and the user has enabled PTZ controls for it. */
export function isPtzActive(cam: {
  name: string;
  ptzCapabilities?: unknown | null;
  isPtzEnabled?: boolean;
}): boolean {
  return isPtzCapable(cam) && Boolean(cam.isPtzEnabled);
}

/**
 * Shape of `camera.ptzCapabilities` as reported by Callhome (device-reported, not
 * user-editable). Not declared on `CallhomeCamera` itself (see the `unknown`-typed
 * workaround on `isPtzCapable`/`isPtzActive` above) — this is the one place that gives it a
 * real type, for the two helpers below that need more than a truthiness check. Derived from
 * the generated `CameraDto` rather than hand-written, so it can't drift from Callhome's
 * actual contract on the next schema regeneration.
 */
export type PtzCapabilities = NonNullable<CameraDto['ptzCapabilities']>;

/**
 * Whether the camera reports any preset support at all (a saved preset, home, or both) —
 * the render-the-presets-button-at-all gate. This is capability metadata only; the actual
 * list of configured presets is a separate, live per-camera query (see PtzController.getPresets).
 */
export function getPtzPresetsSupported(cam: {
  name: string;
  ptzCapabilities?: unknown | null;
}): boolean {
  const capabilities = cam.ptzCapabilities as PtzCapabilities | null | undefined;
  return capabilities?.homeSupported || Boolean(capabilities?.maximumPresets);
}

/** Whether the camera supports a "go to home position" command, independent of named presets. */
export function getPtzHomeSupported(cam: {
  name: string;
  ptzCapabilities?: unknown | null;
}): boolean {
  return Boolean((cam.ptzCapabilities as PtzCapabilities | null | undefined)?.homeSupported);
}

export function getFavoriteCameraGroup(cameraGroups: VideoDiscoveryCameraGroup[]) {
  const favorites = find(cameraGroups, ['name', constants.FAVOURITE_CAMERAS_GROUP_NAME]);
  return favorites || ({} as VideoDiscoveryCameraGroup);
}

export function isFavoriteCameraGroup(name: string) {
  return name === constants.FAVOURITE_CAMERAS_GROUP_NAME;
}

export function getRecentCameraGroup(cameraGroups: CallhomeCameraGroup[]): CallhomeCameraGroup {
  const recent = find(cameraGroups, ['name', constants.RECENT_CAMERAS_GROUP_NAME]);
  return recent || ({} as CallhomeCameraGroup);
}

export function isRecentCameraGroup(name: string) {
  return name === constants.RECENT_CAMERAS_GROUP_NAME;
}

export function isDefaultCameraGroup(name: string) {
  return isFavoriteCameraGroup(name) || isRecentCameraGroup(name);
}

export function isSharedCameraGroup(ownerType?: string) {
  return ownerType === OWNER_TYPE.CUSTOMER;
}

export function isPrivateCameraGroup(ownerType: string) {
  return ownerType === OWNER_TYPE.USER;
}

/**
 * Role camera allowlist from the authorize profile (same source mobile uses).
 * Prefer this over LocationStore.getRestrictedCameras(), which stays empty
 * unless USER_ROLE_CAMERA_RESTRICTIONS is dispatched.
 */
export function getRoleRestrictedCameraIds(
  session: { profile?: { cameraIds?: string[] | null } } | null | undefined,
): string[] | undefined {
  const ids = session?.profile?.cameraIds;
  return ids?.length ? ids : undefined;
}

/**
 * Location-scoped users may only manage shared groups whose cameras are all
 * within locations they can access. Role-based camera allowlists
 * (`profile.cameraIds`) are also enforced when non-empty.
 */
export function userHasAccessToAllCamerasInGroup({
  cameraIds,
  camerasById,
  hasAccessToAllLocations,
  restrictedCameraIds,
}: {
  cameraIds?: readonly string[] | null;
  camerasById: Record<string, unknown>;
  hasAccessToAllLocations: boolean;
  /** Non-empty = role camera allowlist; empty/null/undefined = no camera allowlist. */
  restrictedCameraIds?: readonly string[] | null;
}) {
  if (!cameraIds?.length) return true;

  const inLocationScope =
    hasAccessToAllLocations || cameraIds.every((cameraId) => !!camerasById[cameraId]);
  if (!inLocationScope) return false;

  if (restrictedCameraIds?.length) {
    return cameraIds.every((cameraId) => restrictedCameraIds.includes(cameraId));
  }

  return true;
}

/** Whether a shared camera group can be edited/deleted by the current user. */
export function canManageSharedCameraGroup({
  cameraIds,
  camerasById,
  canCreateSharedCameraGroups,
  hasAccessToAllLocations,
  restrictedCameraIds,
}: {
  cameraIds?: readonly string[] | null;
  camerasById: Record<string, unknown>;
  canCreateSharedCameraGroups: boolean;
  hasAccessToAllLocations: boolean;
  restrictedCameraIds?: readonly string[] | null;
}) {
  return (
    canCreateSharedCameraGroups &&
    userHasAccessToAllCamerasInGroup({
      cameraIds,
      camerasById,
      hasAccessToAllLocations,
      restrictedCameraIds,
    })
  );
}

export function areStreamsBitrateValid(streams: Pick<CallhomeStream, 'average_bps'>[]) {
  let areStreamsBitrateValid = true;

  for (let i = 0; i < streams.length; i++) {
    if (!streams[i].average_bps) {
      areStreamsBitrateValid = false;
      break;
    }
  }

  return areStreamsBitrateValid;
}

export function sortStreamsByQuality<T extends Pick<CallhomeStream, 'average_bps' | 'name'>>({
  streams,
  desc = true,
}: {
  streams: T[];
  desc?: boolean;
}) {
  const ascQuality = areStreamsBitrateValid(streams)
    ? sortBy(streams, ['average_bps'])
    : orderBy(streams, ['name'], ['desc']);

  return desc ? reverse(ascQuality) : ascQuality;
}

/**
 * This function gets the earliest segment date from either a camera, streams, or stream.
 * @param {{streams: {earliestSegmentDate?: number | string}[]} | undefined} camera
 * @returns {number | undefined} a unix timestamp that is earliest `earliestSegmentDate`
 */
export function getEarliestSegmentDate(camera: CallhomeCamera) {
  let cameraEarliestSegmentDate: number | undefined;
  if (!camera || !camera.streams || camera.streams.length === 0) {
    return;
  }

  camera.streams.forEach((stream) => {
    const streamDeviceEarliestSegmentDate = parseInt('' + stream.earliestSegmentDate);
    const streamCloudEarliestSegmentDate = parseInt('' + stream.cloudVMS?.earliestSegmentDate);

    const validDates = [streamDeviceEarliestSegmentDate, streamCloudEarliestSegmentDate].filter(
      (date) => !isNaN(date),
    );

    if (validDates.length > 0) {
      const streamEarliestSegmentDate = Math.min(...validDates);
      if (
        cameraEarliestSegmentDate === undefined ||
        streamEarliestSegmentDate < cameraEarliestSegmentDate
      ) {
        cameraEarliestSegmentDate = streamEarliestSegmentDate;
      }
    }
  });
  return cameraEarliestSegmentDate;
}

/**
 * This function gets the longest retention duration given a camera or streams.
 * @param {Object} camera
 * @param {[Object]} streams
 * @returns {Number} a number in days that is the longest retention period.
 */
export function getLongestRetention({
  camera,
  streams,
}: {
  camera?: CallhomeCamera;
  streams?: CallhomeStream[];
}) {
  if (isEmpty(camera) && isEmpty(streams)) {
    console.warn('Cannot get longest retention value because no camera or streams was provided.');
    return;
  }
  let longestRetention = 0;
  let tempStreams;
  if (!!streams && streams instanceof Array) {
    tempStreams = streams;
  } else {
    // If no stream or streams is provided, try to get it from camera
    tempStreams = get(camera, 'streams', []);
  }
  tempStreams.forEach((stream) => {
    const tempLongestRetention = parseInt('' + stream.retention);
    if (longestRetention === 0 || longestRetention < tempLongestRetention) {
      longestRetention = tempLongestRetention;
    }
  });
  return longestRetention;
}

/**
 * Remaps motion type (person / vehicle / regular) to VMS detection type (people / vehicle / null)
 * @param {MotionType} motionType - type of motion, should be one of the `MOTION_TYPES` constants
 * @returns {'people'|'vehicle'|'dwellTime'|null}
 */
export const remapToVMSDetectionType = (motionType: MotionType | any) =>
  MOTION_TYPE_TO_VMS_DETECTION_TYPE[motionType as MotionType] || null;

/**
 * Remaps VMS detection type (people / vehicle / null) to motion type (person / vehicle / regular)
 * @param {MotionType} detectionType - type of AI detection performed by VMS (null if none is performed)
 */
export const remapVMSDetectionType = (detectionType: string) =>
  (findKey(MOTION_TYPE_TO_VMS_DETECTION_TYPE, (vmsDt) => vmsDt === detectionType) as MotionType) ||
  MOTION_TYPES.REGULAR;

/**
 * @returns {MotionType}
 */
export const getInitialMotionType = (ai?: Record<string, any>) => {
  return get(ai, 'enabled') ? remapVMSDetectionType(ai?.detectionType) : MOTION_TYPES.REGULAR;
};

/**
 * Ensure the current user is allowed to configure the provided motion type. If not, returns the regular motion type.
 * @param {string} motionType current motion type
 * @param {Camera} camera target camera
 */
export const authorizedMotionType = (motionType: MotionType | string) => {
  if (motionType !== MOTION_TYPES.REGULAR && !FeaturesUtil.canModifyMotionDetectionType()) {
    return MOTION_TYPES.REGULAR;
  }
  if (motionType === MOTION_TYPES.DWELL_TIME && !FeaturesUtil.canConfigureDwellTime()) {
    return MOTION_TYPES.REGULAR;
  }
  return motionType;
};
/**
 * Determine the type of motion event produced by this camera, taking into consideration
 * the device parser configuration.
 * AI motion needs an ai filter to be set for this camera in the motion parser,
 * Dwell Time requires an AI parser to be defined on the device
 *
 * @return {MotionType|undefined}
 */
export function determineCameraMotionEventType(
  cameraId: VMSCameraId,
  cameraAi: Record<string, any> | undefined,
  posDevices: CallHomePOS[],
): MotionType {
  const hasMotionParserWithAi =
    posDevices &&
    posDevices.some(
      (posDevice) =>
        posDevice.parserFamilyId &&
        posDevice.parserFamilyId.toUpperCase() === 'MOTION' &&
        get(posDevice, 'metadata.MOTION.cameraAiFilter', []).includes(cameraId),
    );

  if (get(cameraAi, 'enabled')) {
    if (
      get(cameraAi, 'detectionType') === MOTION_TYPE_TO_VMS_DETECTION_TYPE[MOTION_TYPES.DWELL_TIME]
    ) {
      return MOTION_TYPES.DWELL_TIME;
    } else if (hasMotionParserWithAi) {
      return remapVMSDetectionType(cameraAi?.detectionType);
    }
  }

  return MOTION_TYPES.REGULAR;
}

/**
 * Determine the type of dwell time zone the camera is monitoring
 * A camera can monitor more than one zone type in cameraAiSettings.rois
 */
export function determineCameraDwellTimeZone(
  cameraAiSettings: VMSCamera['aiSettings'] | undefined,
) {
  if (!cameraAiSettings || !cameraAiSettings.rois || cameraAiSettings.rois.length === 0) {
    return DWELL_TIME_ZONE_TYPES.SERVICE_WINDOW;
  }
  for (const roi of cameraAiSettings.rois) {
    switch (roi.metadata) {
      case DWELL_TIME_ZONE_TYPES.MENU_BOARD:
        return DWELL_TIME_ZONE_TYPES.MENU_BOARD;
      case DWELL_TIME_ZONE_TYPES.PAYMENT_WINDOW:
        return DWELL_TIME_ZONE_TYPES.PAYMENT_WINDOW;
      case DWELL_TIME_ZONE_TYPES.SERVICE_WINDOW:
        return DWELL_TIME_ZONE_TYPES.SERVICE_WINDOW;
    }
  }

  return DWELL_TIME_ZONE_TYPES.SERVICE_WINDOW;
}
/**
 * @typedef {import('@solinkcorp/api-types').CallhomeCamera} CallhomeCamera
 *
 * @returns {Promise<CallhomeCamera | undefined>}
 */
export async function fetchCameraDataFromDevice({
  authLocation,
  camera,
  fetchParams,
  useProxy,
  deviceId,
}: {
  authLocation: AuthLocation;
  camera: CallhomeCamera;
  fetchParams: Parameters<typeof fetch>;
  useProxy: boolean;
  deviceId: DeviceId;
}) {
  const cameraId = camera?.cameraId;
  const deviceUrl = authLocation?.accessibleAddress;

  if (!deviceUrl || !cameraId) {
    console.warn(
      'Unable to fetch camera data from device directly because device or camera info missing',
    );
    return;
  }

  if (useProxy) {
    return await DeviceProxyApi().getCameraJson(deviceId, cameraId);
  }

  const response = await fetch(...fetchParams);
  if (!response.ok) {
    throw new Error(`Unable to fetch data from ${deviceUrl}: ${response.status}`);
  }

  const body = await response.json();

  return body.camera;
}

/**
 * TODO XIAOKUN: this is not a general method. It can't be used in Alarms page.
 *
 * This function checks if we have camera information that is a result
 * of a fetch directly from the device. If it is not present, we fall back
 * to the camera information from Callhome Location's API. That is from the last checkin
 * so it might not reflect the current camera status.
 *
 * @param {Object} camera the camera object got from the Callhome location's API. data from last checkin
 * @param {Object} cameraMotionParamsFromDevice the motionParams of the camera data fetched from the device. latest data
 * @param {string} lastCameraFetchTimestamp the timestamp of the last camera fetch from the device
 */
export function isMotionZonesSetup({
  camera,
  cameraMotionParamsFromDevice,
  lastCameraFetchTimestamp,
}: {
  camera: CamerasDictionaryCamera;
  cameraMotionParamsFromDevice: CallhomeCamera['motionParams'] | null;
  lastCameraFetchTimestamp: number | null;
}) {
  const { location } = camera;
  const roi = get(camera, 'motionParams.roi');

  if (!isEmpty(cameraMotionParamsFromDevice) && lastCameraFetchTimestamp) {
    const lastCheckin = get(location, 'lastCheckin');
    const latestRoi = get(cameraMotionParamsFromDevice, 'roi');

    if (
      (!!lastCheckin && moment.unix(lastCameraFetchTimestamp).isAfter(moment(lastCheckin))) ||
      !lastCheckin
    ) {
      return {
        isSetup: latestRoi !== ROI_EMPTY,
        isDataFromCheckin: false,
      };
    }
  }

  return {
    isSetup: roi !== ROI_EMPTY,
    isDataFromCheckin: true,
  };
}

/**
 * @return { Promise<boolean> } true if the camera has a defined motion zone.
 */
export async function hasMotionZones(
  authLocation: AuthLocation,
  camera: CallhomeCamera,
  fetchParams: Parameters<typeof fetch>,
  isStandardUser: boolean,
) {
  let answer;

  try {
    // Fetch motion zones directly from the device.  `camera.motionParams.roi` is
    // from the latest checkin, so could be out-of-date.
    const cameraData = await timeout(
      fetchCameraDataFromDevice({
        authLocation,
        camera,
        fetchParams,
        deviceId: camera.deviceId,
        useProxy: isStandardUser,
      }),
      MOTION_ZONE_LOAD_TIMEOUT,
    );

    const latestRoi = cameraData?.motionParams?.roi;

    if (latestRoi) {
      answer = latestRoi !== ROI_EMPTY;
    }
  } catch (err) {
    console.warn(`Could not fetch latest ROI from ${camera.id}`, err);
  }

  // If we couldn't get the latest ROI from the device, fall back to whatever
  // is configured in CallHome.
  if (answer === undefined) {
    const roi = camera.motionParams?.roi;
    answer = !!roi && roi !== ROI_EMPTY;
  }

  return answer;
}

/** Resolve a site camera by CallHome id from the site list and optional dictionary fallback. */
export function findSiteCameraById(
  cameraId: string,
  siteCameras: CallhomeCamera[] | undefined,
  camerasDictionary?: Record<string, CallhomeCamera | undefined>,
): CallhomeCamera | undefined {
  return siteCameras?.find((camera) => camera.id === cameraId) ?? camerasDictionary?.[cameraId];
}

/**
 * Returns the camera links for a given camera.
 * If `viewDisabled` is true, it will return all links regardless of their enabled status
 * otherwise it will only return enabled links.
 * Note: camera is of type any, because many callers of this function do not currently pass in a FloorplanSolinkCamera
 * @param cameraLinks - Optional site camera links when `camera.location.cameraLinks` is stale or unavailable
 * @returns {readonly CallhomeCameraLink[]}
 */
export function getCameraLinks(
  camera: any,
  viewDisabled = false,
  cameraLinks?: CallhomeCameraLink[],
): CallhomeCameraLink[] {
  const locationCameraLinks = cameraLinks ?? camera?.location?.cameraLinks ?? [];
  return locationCameraLinks.filter((link: any) => {
    const cameraId = camera.chCameraId || camera.id;
    const linkMatchesCamera = link.fromCameraId === cameraId;
    return viewDisabled ? linkMatchesCamera : linkMatchesCamera && link.isEnabled;
  });
}

/**
 * Generates a JPG (in the form of a data URL) for every camera in `playingCameras`.
 * This works by finding video elements on the page any copying them into
 * a canvas.
 *
 * @returns {{ playingCamera: PlayingCamera, imageURL: string }[]}
 */
export function getSnapshots(
  customerName: string | undefined,
  playingCameras: PlayingCamera[],
  currentPlayTime: number,
  addWatermark = true,
) {
  return playingCameras.flatMap((playingCamera) => {
    const { camera, location, playerId } = playingCamera;
    let player;
    try {
      const playerContainer = document.getElementById(`player-${playerId}`);
      player = playerContainer?.getElementsByTagName('video')[0];
    } catch (err) {
      console.error('Error getting player to save snapshot from', err);
      return [];
    }

    if (!player) {
      return [];
    }

    const canvas = document.createElement('canvas');
    canvas.width = player.videoWidth;
    canvas.height = player.videoHeight;
    const ctx = canvas.getContext('2d');

    ctx?.drawImage(player, 0, 0, canvas.width, canvas.height);

    if (addWatermark) {
      const fontSize = 12;
      const fontSizeToStringLengthRatio = fontSize - 5;

      const { timezone } = location;
      const snapshotTime = getLocalTime(currentPlayTime, timezone);
      const snapshotDate = getLocalDate(currentPlayTime, timezone);
      const snapshotDateTime = `${snapshotDate} ${snapshotTime}`;

      const rectWidth =
        Math.max(
          customerName?.length ?? 0,
          (camera as any).location.name?.length,
          camera.name.length,
          snapshotDateTime.length,
        ) * fontSizeToStringLengthRatio;

      const rectHeight = 90;
      if (ctx) {
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, rectWidth, rectHeight);
        ctx.globalAlpha = 1;
        ctx.font = `100 ${fontSize}px Arial`;
        ctx.fillStyle = 'white';
      }

      const notes = [customerName, (camera as any).location.name, camera.name, snapshotDateTime];

      notes.forEach((note, noteIndex) => {
        if (note) {
          ctx?.fillText(note, 10, 20 * noteIndex + 20);
        }
      });
    }

    return {
      playingCamera,
      imageURL: canvas.toDataURL('image/jpeg'),
    };
  });
}

export function callhomeCameraToEventCamera(camera: CallhomeCamera) {
  const { id, cameraId, name, thumbnail, streams, annotations, siteId } = camera;
  return {
    id: cameraId,
    chId: id,
    siteId,
    name,
    thumbnail,
    streams,
    annotations,
  } as EventCamera & { siteId: SiteId };
}

/**
 * Returns a tuple of the missing/added camera ids between the two arrays
 * @param {string[]} initialCameraIds
 * @param {string[]} newCameraIds
 * @returns [string[], string[]]
 *  - first element in tuple are initialCameraIds ids that are missing in newCameraIds
 *  - second element in tuple are initialCameraIds ids that have been added to newCameraIds
 */
export function getCameraIdChanges(initialCameraIds: string[], newCameraIds: string[]) {
  const intersectingIds = intersection(initialCameraIds, newCameraIds);

  const removedCameraIds = difference(initialCameraIds, intersectingIds);
  const addedCameraIds = difference(newCameraIds, intersectingIds);

  return [removedCameraIds, addedCameraIds];
}

/**
 *
 * @param {*} LocaleDefinition
 * @param {*} number
 * @param {*} string
 */
export function formatSpeed(locale: LocaleDefinition, bps = 0, option = 'kbps') {
  const speedClass = classifySpeed(bps);
  const number = Math.round(bps / 1000);

  switch (option) {
    case 'kbps':
    default:
      return {
        text: `${locale.cameraList[speedClass]} (${number} kbps)`,
        class: `speed-test ${speedClass}`,
        number,
      };
  }
}

/**
 *
 * @param {*} number
 * @returns 'poor' | 'fair' | 'normal'
 */
function classifySpeed(bps: number) {
  if (bps < constants.POOR_UPLOAD_SPEED) {
    return 'poor';
  }

  if (bps < constants.FAIR_UPLOAD_SPEED) {
    return 'fair';
  }

  return 'normal';
}

export function getCamerasWithRegionsByType(location: VideoDiscoverySite, roiType: string) {
  return location.cameras.filter((camera) =>
    camera.regions.find((region) => region.type === roiType && region.gridRoi !== ROI_EMPTY),
  );
}

export function getDeviceCamerasWithRegionsByType(
  location: VideoDiscoverySite,
  roiType: string,
  deviceId: string,
) {
  return getCamerasWithRegionsByType(location, roiType).filter((c) => c.deviceId === deviceId);
}

export function getOfflineCameras(
  cameras: CallhomeCamera[],
  deviceStatuses: Record<string, constants.DeviceStatus>,
) {
  return cameras.filter(
    (camera) =>
      camera.status !== 'online' ||
      deviceStatuses[camera.deviceId] === constants.DEVICE_STATUS.OFFLINE,
  );
}

export function getOnlineCamerasRatio({
  cameras,
  deviceStatuses,
}: {
  cameras: CallhomeCamera[];
  deviceStatuses: LocationUtil.DeviceStatuses;
}) {
  const camerasCount = cameras.length;

  const offlines = getOfflineCameras(cameras, deviceStatuses);
  const offlineCamerasNumber = offlines.length;

  const camerasRatio =
    offlines.length > 0
      ? `${camerasCount - offlineCamerasNumber}/${camerasCount}`
      : `${camerasCount}/${camerasCount}`;

  return camerasRatio;
}

export type CameraCountStatus = 'all-online' | 'some-offline' | 'all-offline' | 'no-cameras';

/**
 * Structured sibling of `getOnlineCamerasRatio`, for callers that need to render a bare
 * count when healthy and only fall back to a fraction when degraded (e.g. the Aspen
 * `CameraCountText`). `getOnlineCamerasRatio` always returns a fraction and is relied on by
 * legacy `CameraIndicator` / `LocationRow` — left untouched.
 */
export function getCameraCountStatus({
  cameras,
  deviceStatuses,
}: {
  cameras: CallhomeCamera[];
  deviceStatuses: LocationUtil.DeviceStatuses;
}): { status: CameraCountStatus; online: number; total: number } {
  const total = cameras.length;
  const offlines = getOfflineCameras(cameras, deviceStatuses);
  const online = total - offlines.length;

  const status: CameraCountStatus =
    total === 0
      ? 'no-cameras'
      : offlines.length === total
        ? 'all-offline'
        : offlines.length === 0
          ? 'all-online'
          : 'some-offline';

  return { status, online, total };
}
